import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BreadcrumbOverrideProvider, useBreadcrumbTailValue } from '../context/BreadcrumbContext';
import {
  LayoutDashboard, ClipboardList, Boxes, FileText, Package,
  Users as UsersIcon, Calendar, Receipt, Inbox, Clock,
  MessageSquare, LogOut, Search, Menu, X, QrCode, FileBarChart,
  FileBadge2, Warehouse, Link2, FileSignature, Building2, Mail,
  Tag, Bell, Camera, Truck, PoundSterling, Lightbulb, Briefcase, ShieldCheck,
  Wrench, Landmark, Settings, ChevronDown, ChevronRight, ArrowLeft,
  FileEdit, WifiOff, LayoutPanelTop, Megaphone, CalendarClock, History, GraduationCap, User, MonitorSmartphone, MapPin,
} from 'lucide-react';
import LutromLogo from '../components/LutromLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { NotificationBell } from '../components/NotificationBell';
import { ImpersonationBanner } from '../components/ImpersonationBanner';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { SessionsDialog } from '../components/SessionsDialog';

// ───────────────────── Admin navigation — grouped ─────────────────────
// `Overview` is deliberately a root link — the dashboard should always be
// one tap away. Every other destination lives inside a collapsible section
// so the rail can breathe. Item ordering within a section is roughly
// most-used → least-used based on typical field-ops / office work.
const ADMIN_NAV = [
  { to: '/admin', icon: LayoutDashboard, label: 'Overview', end: true },
  {
    key: 'operations',
    label: 'Operations',
    icon: Briefcase,
    children: [
      { to: '/admin/work-orders', icon: ClipboardList, label: 'Work Orders' },
      { to: '/admin/requests', icon: Inbox, label: 'Work Requests' },
      { to: '/admin/schedule', icon: Calendar, label: 'Schedule' },
      { to: '/admin/timesheets', icon: Clock, label: 'Timesheets' },
      { to: '/admin/timesheet-corrections', icon: Clock, label: 'Timesheet Fixes' },
      { to: '/admin/employee-documents', icon: FileText, label: 'Employee Docs' },
      { to: '/admin/compliance', icon: ShieldCheck, label: 'Compliance' },
      { to: '/admin/suggestions', icon: Lightbulb, label: 'Suggestions' },
    ],
  },
  {
    key: 'assets',
    label: 'Assets & Parts',
    icon: Wrench,
    children: [
      { to: '/admin/assets', icon: Boxes, label: 'Assets' },
      { to: '/admin/parts', icon: Package, label: 'Parts Inventory' },
      { to: '/admin/inventory/drafts', icon: Boxes, label: 'Draft Inventory' },
      { to: '/admin/inventory/settings', icon: Tag, label: 'Inventory Settings' },
      { to: '/admin/stores', icon: Warehouse, label: 'Stores' },
      { to: '/admin/qr-codes', icon: QrCode, label: 'QR Codes' },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    icon: Building2,
    children: [
      { to: '/admin/customers', icon: Building2, label: 'Customers' },
      { to: '/admin/quotes', icon: FileSignature, label: 'Quotes' },
      { to: '/admin/certificates', icon: FileBadge2, label: 'Certificates' },
      { to: '/admin/reports', icon: FileBarChart, label: 'Reports' },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    icon: Landmark,
    children: [
      { to: '/admin/invoices', icon: Receipt, label: 'Invoices' },
      { to: '/admin/bills', icon: Mail, label: 'Bills Inbox' },
      { to: '/admin/suppliers', icon: Truck, label: 'Suppliers' },
      { to: '/admin/rate-cards', icon: PoundSterling, label: 'Rate Cards' },
      { to: '/admin/xero', icon: Link2, label: 'Xero' },
    ],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    children: [
      { to: '/admin/marketing/articles', icon: FileText, label: 'Insights Articles' },
      { to: '/admin/marketing/schedule', icon: CalendarClock, label: 'Schedule' },
    ],
  },
  {
    key: 'academy',
    label: 'Lutrom Academy',
    icon: GraduationCap,
    children: [
      { to: '/admin/academy', icon: GraduationCap, label: 'Course Management' },
      { to: '/admin/academy/matrix', icon: GraduationCap, label: 'Training Matrix' },
      { to: '/admin/academy/notifications', icon: GraduationCap, label: 'Notifications' },
    ],
  },
  {
    key: 'system',
    label: 'System',
    icon: Settings,
    children: [
      { to: '/admin/users', icon: UsersIcon, label: 'Users' },
      { to: '/admin/login-activity', icon: History, label: 'Login Activity' },
      { to: '/admin/notifications', icon: Bell, label: 'Notifications' },
      { to: '/admin/settings/pdf-templates', icon: FileEdit, label: 'PDF Templates' },
      { to: '/admin/settings/lists', icon: FileEdit, label: 'Lists' },
      { to: '/admin/settings/address-lookup', icon: MapPin, label: 'Address Lookup' },
    ],
  },
];

const EMPLOYEE_NAV = [
  { to: '/employee', icon: LayoutDashboard, label: 'Overview', end: true },
  { to: '/employee/hub', icon: User, label: 'Employee Hub' },
  { to: '/employee/hub/timesheets', icon: History, label: 'My Timesheets' },
  { to: '/employee/hub/documents', icon: FileText, label: 'My Documents' },
  { to: '/employee/time-clock', icon: Clock, label: 'Time Clock' },
  { to: '/employee/work-orders', icon: ClipboardList, label: 'My Work Orders' },
  { to: '/employee/assets', icon: Boxes, label: 'Assets' },
  { to: '/employee/schedule', icon: Calendar, label: 'My Schedule' },
  { to: '/employee/chat', icon: MessageSquare, label: 'Team Chat' },
  { to: '/employee/receipt-scan', icon: Camera, label: 'Scan Receipt' },
  { to: '/employee/certificates', icon: FileBadge2, label: 'Certificates' },
  { to: '/employee/training', icon: GraduationCap, label: 'My Training' },
  { to: '/employee/notifications', icon: Bell, label: 'Notifications' },
];

// Subcontractors are contract-only field staff — they aren't employed
// by Lutrom so they only need to see the jobs assigned to them, no
// timesheets / rate cards / chat / certificate workflow. The nav is
// intentionally tiny for that reason.
const SUBCONTRACTOR_NAV = [
  { to: '/subcontractor', icon: ClipboardList, label: 'My Work Orders', end: true },
  { to: '/subcontractor/training', icon: GraduationCap, label: 'My Training' },
  { to: '/subcontractor/notifications', icon: Bell, label: 'Notifications' },
];

const CUSTOMER_NAV = [
  { to: '/customer', icon: LayoutDashboard, label: 'Overview', end: true },
  { to: '/customer/requests', icon: Inbox, label: 'Work Requests' },
  { to: '/customer/work-orders', icon: ClipboardList, label: 'Active Jobs' },
  { to: '/customer/schedule', icon: Calendar, label: 'Schedule' },
  { to: '/customer/sites', icon: Building2, label: 'Sites' },
  { to: '/customer/assets', icon: Boxes, label: 'Assets' },
  { to: '/customer/spares', icon: Package, label: 'Spares Stock' },
  { to: '/customer/suggestions', icon: Lightbulb, label: 'Suggestions' },
  { to: '/customer/quotes', icon: FileSignature, label: 'Quotes' },
  { to: '/customer/reports', icon: FileBarChart, label: 'Reports' },
  { to: '/customer/invoices', icon: Receipt, label: 'Invoices' },
  { to: '/customer/notifications', icon: Bell, label: 'Notifications' },
];

const LS_KEY = 'lutrom.sidebar.open-sections';

const NavLeaf = ({ to, icon: Icon, label, end, nested, onNavigate }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onNavigate}
    className={({ isActive }) =>
      `flex items-center gap-3 py-2 rounded-md text-sm font-medium transition-colors ${nested ? 'pl-9 pr-3' : 'px-3'} ${
        isActive
          ? 'lutrom-metal text-white'
          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
      }`
    }
    data-testid={`nav-${to.replace(/^\//, '').replace(/\//g, '-')}`}
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span className="truncate">{label}</span>
  </NavLink>
);

/** Collapsible section in the admin sidebar. Accordion-style: opening
 * a section auto-collapses every other section so only one set of
 * children is visible at a time. State persisted to localStorage. */
const NavSection = ({ section, openKey, setOpenKey, activeKey, onNavigate }) => {
  const Icon = section.icon;
  const isOpen = openKey === section.key || (openKey == null && activeKey === section.key);
  const toggle = () => setOpenKey(isOpen ? null : section.key);
  const isActiveSection = activeKey === section.key;
  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
          isActiveSection && !isOpen ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
        }`}
        data-testid={`nav-section-${section.key}`}
        aria-expanded={isOpen}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left truncate">{section.label}</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
      </button>
      {isOpen && (
        <div className="mt-1 space-y-1" data-testid={`nav-section-${section.key}-items`}>
          {section.children.map(c => (
            <NavLeaf key={c.to} {...c} nested onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
};

const DashboardLayout = () => {
  const { user, logout, offlineSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const online = useOnlineStatus();
  // Hydrate the per-admin "which section is open" preference. Accordion
  // mode: only ONE section can be open at a time. `null` = nothing
  // expanded (auto-derives from active route).
  const [openKey, setOpenKeyState] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Migrate legacy multi-key array → single key (last item wins).
      if (Array.isArray(parsed)) return parsed[parsed.length - 1] || null;
      if (typeof parsed === 'string') return parsed;
      return null;
    } catch {
      return null;
    }
  });
  const setOpenKey = (k) => {
    setOpenKeyState(k);
    try { localStorage.setItem(LS_KEY, JSON.stringify(k)); } catch { /* private mode */ }
  };

  const items = user.role === 'admin'
    ? ADMIN_NAV
    : user.role === 'employee'
      ? EMPLOYEE_NAV
      : user.role === 'subcontractor'
        ? SUBCONTRACTOR_NAV
        : CUSTOMER_NAV;

  // Which section (if any) contains the currently active URL? Used to
  // auto-expand that section even when the admin has manually collapsed
  // everything.
  const activeKey = useMemo(() => {
    const path = location.pathname;
    for (const it of items) {
      if (it.children) {
        const hit = it.children.find(c => path === c.to || path.startsWith(c.to + '/'));
        if (hit) return it.key;
      }
    }
    return null;
  }, [location.pathname, items]);

  // On URL change, auto-open the section that contains the active
  // route. Cheap to do on every navigation since it's just one state
  // write when the section actually changes.
  useEffect(() => {
    if (user.role !== 'admin') return;
    if (activeKey && activeKey !== openKey) setOpenKey(activeKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, user.role]);

  const handleLogout = () => { logout(); navigate('/'); };

  // Each role has a distinct overview page. Hide the back arrow when
  // the admin is already on it — there's nowhere meaningful to go back
  // to from the home dashboard.
  const homePath = user.role === 'admin'
    ? '/admin'
    : user.role === 'employee'
      ? '/employee'
      : user.role === 'subcontractor'
        ? '/subcontractor'
        : '/customer';
  const isHome = location.pathname === homePath || location.pathname === `${homePath}/`;
  const handleNavigate = () => setMobileOpen(false);
  const breadcrumbTailOverride = useBreadcrumbTailValue();

  // Compute breadcrumb trail from the current URL + nav config. Walks
  // into section.children to find the leaf, then any extra path
  // segments beyond the leaf become non-navigable crumbs (e.g. an
  // invoice ID after `/admin/invoices`). Hidden on the role's home.
  const crumbs = useMemo(() => {
    if (isHome) return [];
    const path = location.pathname;
    const out = [];
    const tailToCrumbs = (rest) => rest.split('/').filter(Boolean).map((t) => ({
      label: decodeURIComponent(t).replace(/-/g, ' '),
      to: null,
    }));
    // Match against grouped sections FIRST so a deep route like
    // /admin/invoices/INV-2502 picks the Finance > Invoices leaf, not
    // the flat /admin (Overview) leaf which would also "startsWith".
    for (const it of items) {
      if (!it.children) continue;
      const hit = it.children.find((c) => path === c.to || path.startsWith(c.to + '/'));
      if (hit) {
        out.push({ label: it.label, to: null });
        out.push({ label: hit.label, to: hit.to });
        out.push(...tailToCrumbs(path.slice(hit.to.length)));
        return out;
      }
    }
    // Flat (un-sectioned) leaves. `end` flag means exact-match only —
    // otherwise / would swallow everything under it.
    for (const it of items) {
      if (it.children) continue;
      const exact = path === it.to;
      const matches = it.end ? exact : (exact || path.startsWith(it.to + '/'));
      if (matches) {
        out.push({ label: it.label, to: it.to });
        out.push(...tailToCrumbs(path.slice(it.to.length)));
        return out;
      }
    }
    return out;
  }, [location.pathname, items, isHome]);

  // When a detail page publishes a friendlier label for the final crumb
  // (e.g. "IN202600025" instead of "INV 2815"), swap it in here.
  const displayCrumbs = useMemo(() => {
    if (!breadcrumbTailOverride || crumbs.length === 0) return crumbs;
    const clone = crumbs.slice();
    clone[clone.length - 1] = { ...clone[clone.length - 1], label: breadcrumbTailOverride };
    return clone;
  }, [crumbs, breadcrumbTailOverride]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <ImpersonationBanner />
      {offlineSession && (
        <div className="w-full bg-amber-50 border-b border-amber-200 text-amber-900 text-xs sm:text-sm px-4 py-2 flex items-center gap-2" data-testid="offline-session-banner">
          <WifiOff className="h-4 w-4 shrink-0" />
          <span>You're offline. Showing your saved profile and offline data in read-only mode. We'll reconnect your session as soon as you're back online.</span>
        </div>
      )}
      <SessionsDialog open={devicesOpen} onOpenChange={setDevicesOpen} />
      <div className="flex flex-1 min-h-0">
      <aside
        className={`${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-slate-900 text-slate-100 flex flex-col transition-transform`}
      >
        <div className="h-20 px-6 flex items-center border-b border-slate-800">
          <LutromLogo dark />
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" data-testid="sidebar-nav">
          {items.map(it => {
            if (it.children) {
              return (
                <NavSection
                  key={it.key}
                  section={it}
                  openKey={openKey}
                  setOpenKey={setOpenKey}
                  activeKey={activeKey}
                  onNavigate={handleNavigate}
                />
              );
            }
            return <NavLeaf key={it.to} {...it} onNavigate={handleNavigate} />;
          })}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-blue-700 text-white font-semibold">{user.avatar}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user.name}</div>
              <div className="text-xs text-slate-400 capitalize">{user.role}</div>
            </div>
          </div>
          {!offlineSession && (
            <Button onClick={() => setDevicesOpen(true)} variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800" data-testid="manage-devices-btn">
              <MonitorSmartphone className="h-4 w-4 mr-2" /> Devices
            </Button>
          )}
          <Button onClick={handleLogout} variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800" data-testid="sign-out-btn">
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </Button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 h-16 px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            {!isHome && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  // Use the browser stack when we have one (deep links from
                  // notifications etc. land here with empty history). Fall
                  // back to the role's home page so the arrow is never a
                  // dead-end.
                  if (window.history.length > 1) navigate(-1);
                  else navigate(homePath);
                }}
                title="Back"
                aria-label="Back"
                data-testid="topbar-back-btn"
              >
                <ArrowLeft className="h-5 w-5 text-slate-600" />
              </Button>
            )}
            <div className="hidden lg:flex items-center gap-1.5 text-sm min-w-0" data-testid="topbar-breadcrumbs">
              {displayCrumbs.length === 0 ? (
                <span className="text-slate-500 capitalize">{user.role} Dashboard</span>
              ) : (
                displayCrumbs.map((c, i) => {
                  const last = i === displayCrumbs.length - 1;
                  const node = c.to && !last ? (
                    <NavLink
                      to={c.to}
                      className="text-slate-500 hover:text-slate-900 transition-colors capitalize"
                      data-testid={`breadcrumb-${i}`}
                    >
                      {c.label}
                    </NavLink>
                  ) : (
                    <span
                      className={last ? 'text-slate-900 font-semibold capitalize truncate max-w-[260px]' : 'text-slate-500 capitalize'}
                      data-testid={`breadcrumb-${i}`}
                    >
                      {c.label}
                    </span>
                  );
                  return (
                    <span key={i} className="flex items-center gap-1.5 min-w-0">
                      {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />}
                      {node}
                    </span>
                  );
                })
              )}
            </div>
            {/* Mobile: only show the last (current page) crumb to save space. */}
            {displayCrumbs.length > 0 && (
              <div className="flex lg:hidden text-sm min-w-0" data-testid="topbar-breadcrumb-mobile">
                <span className="text-slate-900 font-semibold capitalize truncate">
                  {displayCrumbs[displayCrumbs.length - 1].label}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!online && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium"
                data-testid="offline-pill"
                title="You are offline — showing cached data. Changes will fail until your connection returns."
              >
                <WifiOff className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Offline</span>
              </span>
            )}
            <div className="hidden lg:flex items-center relative">
              <Search className="h-4 w-4 absolute left-3 text-slate-400" />
              <Input placeholder="Search..." className="pl-9 h-9 w-64 bg-slate-50 border-slate-200" />
            </div>
            <Button variant="ghost" size="icon" className="lg:hidden">
              <Search className="h-5 w-5 text-slate-600" />
            </Button>
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
      </div>
    </div>
  );
};

// Outer wrapper so the page inside <Outlet/> can write into the
// breadcrumb context and the layout above it can read the override.
const DashboardLayoutWithCtx = () => (
  <BreadcrumbOverrideProvider>
    <DashboardLayout />
  </BreadcrumbOverrideProvider>
);

export default DashboardLayoutWithCtx;
